if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        const gallery = this;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          // サムネイル列はバリアント切替時にまるごと差し替わるため、都度取得して常に最新を参照する
          get thumbnails() {
            return gallery.querySelector('[id^="GalleryThumbnails"]');
          },
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // 個別バインドだと差し替え後にクリックが効かなくなるので、イベント委譲にする
        this.addEventListener('click', (event) => {
          const mediaToSwitch = event.target.closest('[data-target]');
          if (!mediaToSwitch || !this.elements.thumbnails?.contains(mediaToSwitch)) return;
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
        });
        this.bindThumbnailWheelScroll();
        this.bindGalleryNav();
        this.bindZoomButton();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // サムネイル列の上でマウスホイールを回したら横にスクロールさせる。
      // ・チルトホイールやトラックパッドの横入力(deltaX)はブラウザ標準に任せる
      // ・左右の端に達したらページのスクロールに戻す（ページが動かなくなるのを防ぐ）
      // media-gallery 自身に張るので、バリアント切替でサムネイルが差し替わっても効き続ける
      //
      // scrollLeft への直接代入は使わない。この列は scroll-behavior:smooth と
      // scroll-snap-type:x mandatory が効いていて、代入するとスナップ位置に戻されるため。
      // scrollBy の behavior:'instant' なら1目盛りずつ確実に動く。
      bindThumbnailWheelScroll() {
        const EDGE_TOLERANCE = 2;

        this.addEventListener(
          'wheel',
          (event) => {
            const list = this.elements.thumbnails?.querySelector('ul');
            if (!list || !list.contains(event.target)) return;

            const max = list.scrollWidth - list.clientWidth;
            if (max <= 0) return;
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

            const atStart = event.deltaY < 0 && list.scrollLeft <= EDGE_TOLERANCE;
            const atEnd = event.deltaY > 0 && list.scrollLeft >= max - EDGE_TOLERANCE;
            if (atStart || atEnd) return;

            event.preventDefault();
            list.scrollBy({ left: event.deltaY, behavior: 'instant' });
          },
          { passive: false }
        );
      }

      // メイン画像の左右送り。イベント委譲なので、バリアント切替で中身が入れ替わっても効く。
      bindGalleryNav() {
        this.addEventListener('click', (event) => {
          const button = event.target.closest('.gallery-nav');
          if (!button || !this.contains(button)) return;
          event.preventDefault();
          this.stepMedia(button.classList.contains('gallery-nav--next') ? 1 : -1);
        });
      }

      // 拡大ボタン。実際にモーダルを開くのは Dawn の .product__media-toggle なので、
      // いま表示中のメディアのトグルを押しに行く。
      bindZoomButton() {
        const zoom = this.querySelector('.gallery-zoom');
        if (!zoom) return;

        zoom.addEventListener('click', (event) => {
          event.preventDefault();
          const active = this.elements.viewer.querySelector('li[data-media-id].is-active') ||
            this.elements.viewer.querySelector('li[data-media-id]');
          active?.querySelector('.product__media-toggle')?.click();
        });
      }

      // 表示中のメディアを direction 分だけ進める（端まで行ったら反対側へ回る）。
      // li だけを対象にする。中の .product__media-toggle も data-media-id を持つため。
      stepMedia(direction) {
        const items = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        if (items.length < 2) return;

        const currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
        const fromIndex = currentIndex === -1 ? 0 : currentIndex;
        const nextIndex = (fromIndex + direction + items.length) % items.length;

        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
